/**
 * CoverThumb – small thumbnail from media_assets (cover).
 * Renders a fixed-size rounded image or a muted fallback.
 * Never renders <img> without src.
 */
import { ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCoverAsset } from '@/hooks/useCoverAsset';
import type { OwnerType } from '@/lib/media/mediaTypes';

interface CoverThumbProps {
  ownerType: OwnerType;
  ownerId: string;
  alt: string;
  className?: string;
  /** Size in px (applied to both width and height). Default 48. */
  size?: number;
  /** Optional preloaded URL to avoid per-row fetches in list views. */
  urlOverride?: string | null;
}

function CoverThumbFallback({ className, size }: { className?: string; size: number }) {
  const sizeStyle = { width: size, height: size, minWidth: size, minHeight: size };

  return (
    <div
      className={cn('rounded-lg bg-muted flex items-center justify-center flex-shrink-0', className)}
      style={sizeStyle}
    >
      <ImageIcon className="h-4 w-4 text-muted-foreground/40" />
    </div>
  );
}

function CoverThumbImage({
  alt,
  className,
  size,
  url,
}: {
  alt: string;
  className?: string;
  size: number;
  url: string;
}) {
  const sizeStyle = { width: size, height: size, minWidth: size, minHeight: size };

  return (
    <img
      src={url}
      alt={alt}
      className={cn('rounded-lg object-cover flex-shrink-0', className)}
      style={sizeStyle}
      loading="lazy"
    />
  );
}

function CoverThumbFromHook({
  ownerType,
  ownerId,
  alt,
  className,
  size,
}: {
  ownerType: OwnerType;
  ownerId: string;
  alt: string;
  className?: string;
  size: number;
}) {
  const { url320 } = useCoverAsset(ownerType, ownerId);

  if (!url320) {
    return <CoverThumbFallback className={className} size={size} />;
  }

  return <CoverThumbImage alt={alt} className={className} size={size} url={url320} />;
}

export function CoverThumb({
  ownerType,
  ownerId,
  alt,
  className,
  size = 48,
  urlOverride,
}: CoverThumbProps) {
  if (urlOverride !== undefined) {
    if (!urlOverride) {
      return <CoverThumbFallback className={className} size={size} />;
    }
    return <CoverThumbImage alt={alt} className={className} size={size} url={urlOverride} />;
  }

  return (
    <CoverThumbFromHook
      ownerType={ownerType}
      ownerId={ownerId}
      alt={alt}
      className={className}
      size={size}
    />
  );
}
